import { ApiResponse, apiClient } from '@api/apiClient';
import { loadModesTypes } from '@common/commonLists';
import { LaneHistorySearchPayload } from '@store/carriers/laneSearch/types';
import { countryToCountryCode } from '@store/orders/services';
import { Lane, LaneHistoryFilters, LaneLocation } from '@store/orders/types';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { LocationSelect } from '@views/carrierMatching/types';
import isEqual from 'lodash/isEqual';

/** Each option sends either daysBack or monthsBack; All time sends neither. */
export const TIME_HORIZON_ITEMS = [
  { label: 'Last 1 day', value: '1d', daysBack: 1, monthsBack: null },
  { label: 'Last 7 days', value: '7d', daysBack: 7, monthsBack: null },
  { label: 'Last 2 weeks', value: '14d', daysBack: 14, monthsBack: null },
  { label: 'Last month', value: '1m', daysBack: null, monthsBack: 1 },
  { label: 'Last 3 months', value: '3m', daysBack: null, monthsBack: 3 },
  { label: 'Last 6 months', value: '6m', daysBack: null, monthsBack: 6 },
  { label: 'Last 1 year', value: '12m', daysBack: null, monthsBack: 12 },
  { label: 'All time', value: 'all', daysBack: null, monthsBack: null },
];

export const DEFAULT_TIME_HORIZON = '1m';

export const getTimeHorizonRange = (timeHorizon: string) => {
  const item = TIME_HORIZON_ITEMS.find(({ value }) => value === timeHorizon);
  return {
    daysBack: item?.daysBack ?? null,
    monthsBack: item?.monthsBack ?? null,
  };
};

export const ALL_MODES = 'All';

export const MODE_ITEMS = [
  { label: 'All Modes', value: ALL_MODES },
  ...loadModesTypes.map((mode) => ({ label: mode, value: mode })),
];

export const PAGE_SIZE = 100;

export const DEFAULT_RADIUS_MILES = 0;

export const MAX_RADIUS_MILES = 500;

const DEFAULT_COUNTRY = 'US';

const toCountryCode = (country: string | null | undefined) => {
  if (!country) {
    return DEFAULT_COUNTRY;
  }

  const mapped = countryToCountryCode[
    country.toLowerCase() as keyof typeof countryToCountryCode
  ] as string | undefined;
  const code = mapped ?? country;

  // The country dropdown uses MEX for Mexico, not MX.
  return code.toUpperCase() === 'MX' ? 'MEX' : code;
};

export const EMPTY_LOCATION: LaneLocation = {
  city: '',
  state: '',
  zip: '',
  country: DEFAULT_COUNTRY,
  lat: 0,
  long: 0,
};

export const hasLocation = (location: LaneLocation) =>
  Boolean(location.city) && Boolean(location.state);

export const formatLocation = (location: LaneLocation) =>
  location.state ? `${location.city}, ${location.state}` : location.city;

export const formatAutocompleteValue = (location: LaneLocation) =>
  hasLocation(location)
    ? `${location.city} ${location.state} ${location.zip}`.trim()
    : '';

export const toLaneLocation = (item: LocationSelect): LaneLocation => ({
  city: item.city,
  state: item.state,
  zip: item.zipcode,
  country: toCountryCode(item.country),
  lat: item.lat,
  long: item.long,
});

export const getLoadLane = (load: ILoadEditViewModel): Lane | null => {
  const stops = (load.stops ?? []).filter(
    (stop) => stop.operationType !== 'Delete',
  );
  let origin: LaneLocation | undefined;
  let destination: LaneLocation | undefined;

  stops.forEach((stop) => {
    const location: LaneLocation = {
      city: stop.city ?? '',
      state: stop.state ?? '',
      zip: stop.zip,
      country: toCountryCode(stop.countryCode),
      lat: stop.lat ?? 0,
      long: stop.long ?? 0,
    };

    if (!origin && (Boolean(stop.origin) || stop.pickUp)) {
      origin = location;
    }

    if (Boolean(stop.destination) || stop.dropOff) {
      destination = location;
    }
  });

  if (
    !origin ||
    !destination ||
    !hasLocation(origin) ||
    !hasLocation(destination)
  ) {
    return null;
  }
  return { origin, destination };
};

const isSearchableLocation = (location: LaneLocation) =>
  hasLocation(location) && (location.lat !== 0 || location.long !== 0);

export const toLaneHistoryPayload = (
  filters: LaneHistoryFilters,
): LaneHistorySearchPayload => ({
  originLatitude: filters.origin.lat,
  originLongitude: filters.origin.long,
  originRadius: String(filters.originRadiusMiles),
  destinationLatitude: filters.destination.lat,
  destinationLongitude: filters.destination.long,
  destinationRadius: String(filters.destinationRadiusMiles),
  ...getTimeHorizonRange(filters.timeHorizon),
  equipment: [...filters.equipment],
  mode: filters.mode === ALL_MODES ? null : filters.mode,
});

export const isSamePayload = (
  a: LaneHistorySearchPayload,
  b: LaneHistorySearchPayload,
) =>
  isEqual(
    { ...a, equipment: [...a.equipment].sort() },
    { ...b, equipment: [...b.equipment].sort() },
  );

export const canSearch = (filters: LaneHistoryFilters) =>
  hasLocation(filters.origin) && hasLocation(filters.destination);

export const resolveLocationCoordinates = async (
  location: LaneLocation,
): Promise<LaneLocation | null> => {
  if (isSearchableLocation(location)) {
    return location;
  }

  if (!hasLocation(location)) {
    return null;
  }

  try {
    const res: ApiResponse<LocationSelect[]> = await apiClient.get(
      `capacity/autocomplete?country=${location.country}&term=${encodeURIComponent(location.zip || `${location.city} ${location.state}`)}`,
    );

    if (res.isSuccess && res.value.length > 0) {
      const { lat, long } = toLaneLocation(res.value[0]);

      return { ...location, lat, long };
    }

    return null;
  } catch (e) {
    logError('laneHistoryResolveLocationCoordinates', e as Error);
    throw createError(e, 'Could not look up the lane location.');
  }
};
